import type {
  AssessmentResponse,
  ContactRequest,
  ContactResponse,
  WorkflowAssessmentRequest,
} from '@/types/assessment';

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api';

/**
 * POST a workflow assessment to the FastAPI backend.
 *
 * The same endpoint serves both phases:
 *   - Phase 1 (preview): omit `contact` → backend returns the generated map,
 *     no Lead is created.
 *   - Phase 2 (lead): include `contact` and the `assessment_id` from phase 1
 *     → backend creates the ERPNext Lead and starts Flowable.
 *
 * Errors are surfaced as thrown `Error`s with a user-safe message.
 */
export async function postWorkflowAssessment(
  body: WorkflowAssessmentRequest,
  opts: { signal?: AbortSignal } = {},
): Promise<AssessmentResponse> {
  const url = `${API_BASE}/v1/workflow-assessment`;

  let resp: Response;
  try {
    resp = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
      signal: opts.signal,
    });
  } catch (e) {
    // Network failure (DNS, offline, CORS preflight blocked, etc.)
    throw new Error('Network unavailable. Please try again in a moment.');
  }

  if (resp.status === 429) {
    throw new Error('Too many requests. Please wait a minute and try again.');
  }

  if (resp.status === 422) {
    // Pydantic validation error → tell the user which field, when possible.
    let msg = 'Some inputs are invalid. Please review the form.';
    try {
      const data = await resp.json();
      if (Array.isArray(data?.detail) && data.detail.length > 0) {
        const first = data.detail[0];
        const field = Array.isArray(first.loc) ? first.loc.join('.') : 'input';
        msg = `Invalid ${field}: ${first.msg ?? 'check this field'}.`;
      }
    } catch {
      /* ignore parse errors, use generic msg */
    }
    throw new Error(msg);
  }

  if (!resp.ok) {
    throw new Error(
      `The assessment service returned ${resp.status}. Please try again.`,
    );
  }

  return (await resp.json()) as AssessmentResponse;
}

/**
 * POST a direct contact ("Hablemos") to the FastAPI backend.
 *
 * Creates an ERPNext Lead without going through the workflow mapper. Errors are
 * surfaced as thrown `Error`s with a user-safe message, matching
 * postWorkflowAssessment's contract.
 */
export async function postContact(
  body: ContactRequest,
  opts: { signal?: AbortSignal } = {},
): Promise<ContactResponse> {
  const url = `${API_BASE}/v1/contact`;

  let resp: Response;
  try {
    resp = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
      signal: opts.signal,
    });
  } catch (e) {
    throw new Error('Network unavailable. Please try again in a moment.');
  }

  if (resp.status === 429) {
    throw new Error('Too many requests. Please wait a minute and try again.');
  }

  if (resp.status === 422) {
    let msg = 'Some inputs are invalid. Please review the form.';
    try {
      const data = await resp.json();
      if (Array.isArray(data?.detail) && data.detail.length > 0) {
        const first = data.detail[0];
        const field = Array.isArray(first.loc) ? first.loc.join('.') : 'input';
        msg = `Invalid ${field}: ${first.msg ?? 'check this field'}.`;
      }
    } catch {
      /* ignore parse errors, use generic msg */
    }
    throw new Error(msg);
  }

  if (!resp.ok) {
    throw new Error(`The contact service returned ${resp.status}. Please try again.`);
  }

  return (await resp.json()) as ContactResponse;
}
